import { FC, useEffect, useState } from 'react'
import Modal from '../Modal'
import IconButton from '../buttons/IconButton'
import { DndContext, useSensor, useSensors, PointerSensor, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-hot-toast'

import { ReactComponent as HamburgerIcon } from '../../assets/svg/hamburger.svg'
import { ReactComponent as InfoIcon } from '../../assets/svg/info.svg'
import CustomStepDropDown from './CustomStepDropdown'
import ParameterPresetManager from './parameter/ParameterPresetManager'
import { MiscellaneousParameterList } from '@aero-platform/shared'
import { BasicNameValue, StarCCMConfig, StarCCMParameterList } from '../../types/types'
import { useGetStarCCMParameterPresets, useGetMiscParameterPresets, useCreateMiscParameterPreset, useCreateStarCCMParameterPreset } from '../../api/runconfig-API';
import { protoService } from '../../proto/ProtoService';
import { SaveIcon } from '../Icons';
import { HttpStatusCode } from 'axios';
import { starCCMMacroSteps } from '../../utils/constants';


type RunConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedSteps: BasicNameValue[];
  selectedStarCCMPreset: StarCCMParameterList;
  selectedMiscPreset: MiscellaneousParameterList;
  raceTeamId: string;
  customMacros: string[];
  saveStarCCMConfig: (config: StarCCMConfig) => Promise<void>;
};

const RunConfigModal: FC<RunConfigModalProps> = ({
  isOpen,
  onClose,
  selectedSteps = [],
  selectedStarCCMPreset,
  selectedMiscPreset,
  raceTeamId,
  customMacros,
  saveStarCCMConfig
}) => {
  // Fetches data and populates states from server
  const [, fetchStarccmPresets] = useGetStarCCMParameterPresets()
  const [, fetchMiscPresets] = useGetMiscParameterPresets()
  const [, createStarCCMPreset] = useCreateStarCCMParameterPreset()
  const [, createMiscPreset] = useCreateMiscParameterPreset()

  const [allStarCCMPresets, setAllStarCCMPresets] = useState<StarCCMParameterList[]>([])
  const [allMiscPresets, setAllMiscPresets] = useState<MiscellaneousParameterList[]>([])
  const [selectedStarCCMPresetLocal, setSelectedStarCCMPresetLocal] = useState<StarCCMParameterList>(selectedStarCCMPreset)
  const [selectedMiscPresetLocal, setSelectedMiscPresetLocal] = useState<MiscellaneousParameterList>(selectedMiscPreset)

  const [defaultMacroSteps, setDefaultMacroSteps] = useState<BasicNameValue[]>([])
  const [additionalMacroSteps, setAdditionalMacroSteps] = useState<BasicNameValue[]>([])
  const [steps, setSteps] = useState<BasicNameValue[]>([])
  const [additionalSteps, setAdditionalSteps] = useState<BasicNameValue[]>([])

  // Macro steps (unchanged)
  useEffect(() => {
    starCCMMacroSteps.forEach(step => {
      setDefaultMacroSteps(prev => [...prev, new BasicNameValue({ name: step.name, value: step.value })]);
    });

    // setAdditionalMacroSteps([
    // new BasicNameValue({ name: "Custom Step 1", value: "Custom Step 1" }),
    // ]);

    setAdditionalMacroSteps(customMacros.map(macro => new BasicNameValue({ name: macro, value: macro })));
  }, []);

  // Fetch StarCCM presets
  useEffect(() => {
    const fetchStarccmPresetsList = async () => {
      const starccmPresets: StarCCMParameterList[] = [];
      try {
        const starccmPresetRes = await fetchStarccmPresets();
        if (starccmPresetRes.data.protoBytesList && starccmPresetRes.data.protoBytesList.length > 0) {
          starccmPresetRes.data.protoBytesList.forEach((item) => {
            const preset = protoService.deSerialize(item, 'starccmparameterlist') as StarCCMParameterList;
            if (preset) {
              starccmPresets.push(preset);
            }
          });
        }
      } catch { }
      setAllStarCCMPresets(prev => [...prev, ...starccmPresets]);
    };
    fetchStarccmPresetsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Miscellaneous presets
  useEffect(() => {
    const fetchMiscPresetsList = async () => {
      let miscPresets: MiscellaneousParameterList[] = [];
      try {
        const miscPresetRes = await fetchMiscPresets();
        if (miscPresetRes.data.protoBytesList && Array.isArray(miscPresetRes.data.protoBytesList) && miscPresetRes.data.protoBytesList.length > 0) {
          miscPresetRes.data.protoBytesList.forEach((item) => {
            const preset = protoService.deSerialize(item, 'miscellaneousparameterlist') as MiscellaneousParameterList;
            if (preset) {
              miscPresets.push(preset);
            }
          });
        }
      } catch { }
      setAllMiscPresets(prev => [...prev, ...miscPresets]);
    };
    fetchMiscPresetsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Always derive additionalSteps from selectedMacroSteps and defaultMacroSteps
    if (selectedSteps?.length > 0 && defaultMacroSteps.length > 0) {
      const genAddSteps = selectedSteps.filter(
        step => !defaultMacroSteps.some(
          defaultStep => defaultStep.name === step.name && defaultStep.value === step.value
        )
      );
      setAdditionalSteps(genAddSteps);
      setSteps(selectedSteps);
    } else {
      setSteps([]);
    }
    // Only run when selectedSteps changes, and when defaultMacroSteps is populated
  }, [selectedSteps, defaultMacroSteps.length]);

  useEffect(() => {
    if (defaultMacroSteps.length > 0) {
      setAdditionalSteps(
        steps.filter(
          step => !defaultMacroSteps.some(
            defaultStep => defaultStep.name === step.name && defaultStep.value === step.value
          )
        )
      );
    }
  }, [steps, defaultMacroSteps.length]);

  // Set selected Star Preset
  useEffect(() => {
    if (selectedStarCCMPreset) {
      setSelectedStarCCMPresetLocal(selectedStarCCMPreset)
    }
  }, [selectedStarCCMPreset])

  // Set selected Misc Preset
  useEffect(() => {
    if (selectedMiscPreset) {
      setSelectedMiscPresetLocal(selectedMiscPreset)
    }
  }, [selectedMiscPreset])

  // Sortable item for macro steps
  const SortableMacroStep = ({ id, step, index, isDraggable }) => {
    const sortable = useSortable({ id });
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
    // Prevent overflow to the right by clamping translateX to 0 or less
    let clampedTransform = transform;
    if (transform && transform.x > 0) {
      clampedTransform = { ...transform, x: 0 };
    } else if (transform && transform.x < 0) {
      clampedTransform = { ...transform, x: 0 };
    }
    return (
      <div
        ref={setNodeRef}
        className={`
                    flex items-center min-h-[32px] mb-2 px-4 py-2 rounded-lg
                    font-semibold flex-1
                    ${isDragging ? 'bg-[#e0e7ef] shadow-lg' : (isDraggable ? 'bg-gm-aqua-light text-white cursor-grab' : 'bg-gm-blue text-white cursor-not-allowed select-none')}
                    overflow-hidden
                `}
        style={{
          transition,
          transform: CSS.Transform.toString(clampedTransform),
        }}
        {...(isDraggable ? attributes : {})}
        {...(isDraggable ? listeners : {})}
      >
        <div className="flex font-semibold flex-1 gap-x-2 items-center">
          {isDraggable && (
            <span className="flex items-center justify-center">
              <HamburgerIcon className="w-2 h-2 flex-1 fill-white" />
            </span>
          )}
          <span className="flex flex-1 self-center">{step.name}</span>
        </div>
      </div>
    );
  };

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const sensors = useSensors(pointerSensor);

  // Helper to get unique sortable IDs
  const getSortableId = (step: BasicNameValue, idx: number) => step.value !== "" && `${step.value}-${idx}`;

  const updateSelectedStarCCMPresetLocal = async (preset: StarCCMParameterList) => {
    try {
      preset.isPreset = true;
      preset.raceteamId = raceTeamId;
      // Serialize the preset to protoBytes
      await createStarCCMPreset({
        data: protoService.encodePayload(preset, 'starccmparameterlist')
      }).then(() => {
        toast.success(`Successfully Created: ${preset.name}`);
      }).catch((error) => {
        toast.error(`Failed to create preset: ${preset.name}. Message: ${error}`);
      });
    } catch (error) {
      console.error("Error creating StarCCM preset:", error);
    }
  };

  const updateSelectedMiscPresetLocal = async (preset: MiscellaneousParameterList) => {
    // setSelectedLocalMiscPreset(preset);
    try {
      preset.isPreset = true;
      preset.raceteamId = raceTeamId;
      await createMiscPreset({
        data: protoService.encodePayload(preset, 'miscellaneousparameterlist')
      }).then((value) => {
        if (value.status === HttpStatusCode.Ok) {
          // Handle Saving Data
          toast.success(`Successfully created misc preset: ${preset.name}`);
        } else {
          toast.error(`Failed to create misc preset: ${preset.name}`);
          toast.error(`Message: ${value.data.message}`);
        }
      }).catch((error) => {
        console.error("Error creating miscellaneous preset:", error);
        toast.error(`Failed to create preset: ${preset.name}`);
      });
    } catch (error) {
      console.error("Error creating miscellaneous preset:", error);
    }
  };

  const saveRunConfig = async () => {
    try {
      const starMiscParams = {};
      if (selectedStarCCMPresetLocal) {
        starMiscParams['starccmParameters'] = new StarCCMParameterList({
          raceteamId: raceTeamId,
          name: selectedStarCCMPresetLocal.name,
          paramList: selectedStarCCMPresetLocal.paramList.map(param => new BasicNameValue({ name: param.name, value: param.value })),
          isPreset: true
        });
      }
      if (selectedMiscPresetLocal) {
        starMiscParams['miscellaneousParameters'] = new MiscellaneousParameterList({
          raceteamId: raceTeamId,
          name: selectedMiscPresetLocal.name,
          paramList: selectedMiscPresetLocal.paramList.map(param => new BasicNameValue({ name: param.name, value: param.value })),
          isPreset: true
        });
      }
      const newStarCCMConfig: StarCCMConfig = new StarCCMConfig({
        raceTeamId: raceTeamId,
        macroSteps: steps.map(step => new BasicNameValue({ name: step.name, value: step.value })),
        customSteps: steps
          .filter(
            step => !defaultMacroSteps.some(ds => ds.name === step.name && ds.value === step.value)
          )
          .map(step => step.value),
        stepOrder: steps.map(step => step.value),
        ...starMiscParams
      });
      await saveStarCCMConfig(newStarCCMConfig).catch((error) => {
        console.error('Error saving StarCCM configuration:', error);
        toast.error(`Failed to save StarCCM configuration. Message: ${error}`);
      });
    } catch (error) {
      console.error('Error saving StarCCM configuration:', error);
      toast.error(`Failed to save StarCCM configuration. Message: ${error}`);
    }
  };

  return (
    <Modal
      title="Edit StarCCM Run Configuration"
      titleAdditionalClass='font-extrabold tracking-wide'
      isOpen={isOpen}
      withAction={false}
      easyClose={true}
      onCancel={onClose}
    >
      <div className="w-full flex items-start min-h-[300px] max-h-[60vh] tracking-wide overflow-x-hidden">
        <div className="w-full mr-2 flex flex-col items-stretch min-h-full max-h-full">
          <div className="flex flex-row gap-4 w-full my-4 ml-2 grow overflow-y-auto">
            {/* Section 1 */}
            <div className='space-y-4 w-3/4'>
              <div id="macro-steps">
                <label htmlFor="macro-steps" className="block text-lg font-medium">
                  <span>StarCCM Macro Steps Selection</span>
                </label>
                {/* Checkbox with selected */}
                <div className="grid grid-cols-4 gap-x-8 gap-y-4 items-end">
                  {defaultMacroSteps.map((step, index) => (
                    <div key={index} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id={`macro-step-${index}`}
                        checked={steps.some(s => s.name === step.name && s.value === step.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSteps((prev) => {
                              if (!prev) return [step];
                              // Separate default and custom steps
                              const prevDefaultSteps = prev.filter(s =>
                                defaultMacroSteps.some(ds => ds.value === s.value)
                              );
                              // If step already present, do nothing
                              if (prevDefaultSteps.some(s => s.value === step.value)) return prev;
                              // Find the index in defaultMacroSteps for the new step
                              const insertIdx = defaultMacroSteps.findIndex(ds => ds.value === step.value);

                              // Find where to insert in prev to preserve overall order
                              let newSteps = [...prev];
                              // Find the index in prev where the next default step (after insertIdx) is located
                              let nextDefaultStepIdx = -1;
                              for (let i = 0; i < newSteps.length; i++) {
                                const s = newSteps[i];
                                const idxInDefault = defaultMacroSteps.findIndex(ds => ds.value === s.value);
                                if (idxInDefault > insertIdx) {
                                  nextDefaultStepIdx = i;
                                  break;
                                }
                              }
                              if (nextDefaultStepIdx === -1) {
                                // Insert at the end (before custom steps if any)
                                newSteps.push(step);
                              } else {
                                // Insert at the found index, shifting custom steps if needed
                                newSteps.splice(nextDefaultStepIdx, 0, step);
                              }
                              return newSteps;
                            });
                          } else {
                            setSteps((prev) => prev.filter(s => s.name !== step.name && s.value !== step.value))
                          }
                        }}
                        className="mr-2 h-4 w-4 text-gm-blue border-gray-300 rounded-full cursor-pointer accent-gm-blue"
                      />
                      <label htmlFor={`macro-step-${index}`} className="cursor-pointer flex items-center h-full">
                        <span className={steps.includes(step) ? 'font-bold' : 'font-normal'}>{step.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div id="custom-macro-steps">
                <label htmlFor="custom-macro-steps" className="block text-lg font-medium mt-4">
                  <span>AeroCCM Custom Step (.java file)</span>
                </label>
                <div className="w-[100%] space-y-4">
                  {additionalSteps.length === 0 ? (
                    <CustomStepDropDown
                      step={new BasicNameValue({ name: "", value: "" })}
                      index={0}
                      additionalMacroSteps={additionalMacroSteps}
                      setSteps={setSteps}
                      displayAdd={true} // Always show the add button for the first empty step
                    />
                  ) : (
                    additionalSteps.map((step, index) => (
                      <CustomStepDropDown
                        key={`selected-${index}`}
                        step={step}
                        index={index}
                        additionalMacroSteps={additionalMacroSteps}
                        setSteps={setSteps}
                        displayAdd={index >= additionalSteps.length - 1}
                      />
                    ))
                  )}
                </div>
              </div>
              <div id="parameters-presets">
                <label htmlFor="parameters-presets" className="block text-lg font-medium mt-2">
                  <span>StarCCM Parameters Presets</span>
                </label>
                <div id="save-preset" className="w-[100%]">
                  <ParameterPresetManager
                    presetType={"StarCCMParameterList"}
                    currentPreset={selectedStarCCMPresetLocal}
                    presets={allStarCCMPresets}
                    updateSelectedPreset={updateSelectedStarCCMPresetLocal}
                    updateCurrentSelectedPreset={(preset) => setSelectedStarCCMPresetLocal(preset as StarCCMParameterList)}
                  />
                </div>
              </div>
              <div id="misc-parameters-presets">
                <label htmlFor="parameters-presets" className="block text-lg font-medium mt-2">
                  <span>Misc Parameters Presets</span>
                </label>
                <ParameterPresetManager
                  presetType={"MiscellaneousParameterList"}
                  currentPreset={selectedMiscPresetLocal}
                  presets={allMiscPresets}
                  updateSelectedPreset={updateSelectedMiscPresetLocal}
                  updateCurrentSelectedPreset={(preset) => setSelectedMiscPresetLocal(preset as MiscellaneousParameterList)}
                />
              </div>
            </div>
            {/* Section 2 */}
            <div className='w-1/4 mt-6'>
              {/* Drag and Drop based on selected steps */}
              <h3 className="text-lg font-medium inline-flex">Selected Steps
                <IconButton
                  title="The steps you have selected for this run. Drag to reorder."
                  additionalClassName=' inline'
                  severity="help"
                  clear
                  icon={<InfoIcon className="w-6 h-6 dark:fill-white fill-gm-black inline cursor-pointer" />}
                />
              </h3>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => {
                const { active, over } = event;
                if (active && over && active.id !== over.id) {
                  // Extract index from unique id
                  const getIndex = (id) => parseInt(id.split('-').pop(), 10);
                  const oldIndex = getIndex(active.id);
                  const newIndex = getIndex(over.id);
                  setSteps((prev) => arrayMove(prev, oldIndex, newIndex));
                }
              }}>
                <SortableContext items={steps.filter(step => step.value !== "").map(getSortableId)} strategy={verticalListSortingStrategy}>
                  <div
                    className="flex flex-col gap-2 dark:bg-neutral-900 rounded-lg p-4 max-h-[400px] overflow-y-auto"
                  >
                    {steps.filter(step => step.value !== "").length === 0 ? (
                      <div className="text-neutral-400 text-center py-8">No steps selected. Use the checkboxes to add steps.</div>
                    ) : (
                      steps
                        .map((step, idx) => ({ step, idx }))
                        .filter(({ step }) => step.value !== "")
                        .map(({ step, idx }) => (
                          <SortableMacroStep
                            key={getSortableId(step, idx)}
                            id={getSortableId(step, idx)}
                            step={step}
                            index={idx}
                            isDraggable={!defaultMacroSteps.find(defaultStep => defaultStep.value === step.value)}
                          />
                        ))
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>
          <div className='w-[100%] flex items-center justify-end basis-10'>
            <IconButton
              title="Save Configuration"
              label='Save'
              additionalClassName=' !w-fit items-center !bg-gm-blue !p-2 text-sm'
              icon={<SaveIcon className="flex self-center w-4 h-4 fill-white" />}
              onClick={saveRunConfig}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RunConfigModal
